import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SecondaryButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Users, Eye } from 'lucide-react';
import { useSuggestedUsers } from '@/hooks/useSuggestedUsers';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

const SuggestedUsersSection = () => {
  const { suggestedUsers, loading, handleFollowUser } = useSuggestedUsers();
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const displayedUsers = showAll ? suggestedUsers : suggestedUsers.slice(0, 4);

  const handleUserClick = (username: string) => {
    navigate(`/app/user/${username}`);
  };

  if (loading) {
    return (
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="text-white">Utenti Suggeriti</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex flex-col space-y-3 p-3 rounded-lg glass border border-white/5">
                <div className="flex items-center space-x-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (suggestedUsers.length === 0) {
    return (
      <Card className="glass border-white/20">
        <CardContent className="p-8 text-center">
          <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            Nessun utente suggerito al momento
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-strong border-border/30 animate-fade-in">
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-foreground/95 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          Utenti Suggeriti
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {displayedUsers.map((user, index) => (
            <div 
              key={user.user_id} 
              className="flex flex-col space-y-3 p-3 sm:p-4 rounded-xl glass border border-border/20 hover:border-primary/30 transition-all duration-300 hover-scale animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center space-x-3">
                <Avatar className="h-12 w-12 ring-2 ring-primary/20 hover:ring-primary/40 transition-all">
                  <AvatarImage src={user.avatar_url || undefined} className="object-cover" />
                  <AvatarFallback className="bg-gradient-primary text-white font-medium">
                    {user.display_name?.[0] || user.username?.[0] || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p 
                    className="text-sm font-semibold truncate text-foreground/95 hover:text-primary cursor-pointer transition-colors"
                    onClick={() => handleUserClick(user.username)}
                  >
                    {user.display_name || user.username}
                  </p>
                  <p className="text-xs text-muted-foreground/80">
                    {user.posts_count} post • {user.pets_count} pet
                  </p>
                </div>
              </div>
              <SecondaryButton 
                size="default"
                className="w-full justify-center h-10 text-sm px-3 hover-scale transition-elastic"
                onClick={() => handleFollowUser(user.user_id)}
              >
                <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="truncate">Segui</span>
              </SecondaryButton>
            </div>
          ))}
        </div>
        
        {suggestedUsers.length > 4 && (
          <div className="mt-6 text-center">
            <SecondaryButton
              variant="outline"
              size="default"
              onClick={() => setShowAll(!showAll)}
              className="border-border/30 hover:border-primary/30 h-10 px-6 text-sm hover-scale transition-elastic"
            >
              <Eye className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{showAll ? 'Mostra Meno' : 'Vedi Altro'}</span>
            </SecondaryButton>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SuggestedUsersSection;